import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ApiService, TokenManager, SESSION_EXPIRED_EVENT } from '../utils/api';

const AuthContext = createContext();

// Persist the last successful user profile so role-gated pages keep working
// offline (e.g. the QR scan flow). The JWT payload does not carry roles, so
// without this cache an offline reload downgrades the user to "None".
const CACHED_USER_KEY = 'cachedUser';
const cacheUser = (user) => {
  try {
    if (user) localStorage.setItem(CACHED_USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(CACHED_USER_KEY);
  } catch (_) { /* storage unavailable */ }
};
const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      try {

        const hasToken = TokenManager.isAuthenticated();

        
        if (hasToken) {

          try {
            const userData = await ApiService.getCurrentUser();
            setUser(userData);
            setIsAuthenticated(true);
            cacheUser(userData);
          } catch (error) {
            // Be tolerant of transient or non-auth errors so users don't get kicked
            console.error('AuthContext - getCurrentUser failed:', error);

            const message = error?.message || '';
            const isAuthError = message.toLowerCase().includes('authentication required') ||
                                message.toLowerCase().includes('unauthorized');

            if (isAuthError) {
              // Hard auth failure – clear token and treat as logged out
              TokenManager.removeToken();
              setUser(null);
              setIsAuthenticated(false);
            } else {
              // Network / server issues – restore the last known profile (with
              // roles) so offline reloads don't lose access. Fall back to the
              // token payload only if we have no cached profile.
              const cached = readCachedUser();
              if (cached) {
                setUser(cached);
                setIsAuthenticated(true);
              } else {
                const payload = TokenManager.getTokenPayload();
                if (payload) {
                  setUser({
                    id: payload.sub || payload.userId || null,
                    email: payload.email || '',
                    roles: payload.roles || [],
                  });
                  setIsAuthenticated(true);
                } else {
                  setIsAuthenticated(true); // token is structurally valid; allow access
                }
              }
            }
          }
        } else {
          console.log('AuthContext - No valid token found');
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        // In this outer catch, avoid aggressively logging the user out; just mark unauthenticated
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
        console.log('AuthContext - Initialization complete');
      }
    };

    initAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await ApiService.login(credentials);
      
      TokenManager.setToken(response.token);
      setUser(response.user);
      setIsAuthenticated(true);
      cacheUser(response.user);
      
      return { success: true, user: response.user };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await ApiService.register(userData);
      
      TokenManager.setToken(response.token);
      setUser(response.user);
      setIsAuthenticated(true);
      cacheUser(response.user);
      
      return { success: true, user: response.user };
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(() => {
    TokenManager.removeToken();
    cacheUser(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Any request rejected with 401 (token revoked by a password change or "sign out everywhere", or expired).
  useEffect(() => {
    window.addEventListener(SESSION_EXPIRED_EVENT, logout);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, logout);
  }, [logout]);

  // Signs in with a token issued elsewhere, e.g. an event's team sign-in page.
  const startSession = (token, nextUser) => {
    TokenManager.setToken(token);
    setUser(nextUser);
    setIsAuthenticated(true);
    cacheUser(nextUser);
  };

  // Keeps this session signed in after a password change that revoked older tokens.
  const replaceSession = (token, nextUser) => {
    TokenManager.setToken(token);
    if (nextUser) {
      setUser(nextUser);
      cacheUser(nextUser);
    }
  };

  const logoutEverywhere = async () => {
    await ApiService.logoutEverywhere();
    logout();
  };

  const hasRole = (role) => {
    return user?.roles?.includes(role) || false;
  };

  const isAdmin = () => hasRole('ADMIN');
  const isEditor = () => hasRole('EDITOR') || isAdmin();
  const isViewer = () => hasRole('VIEWER') || isEditor();

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    logoutEverywhere,
    replaceSession,
    startSession,
    hasRole,
    isAdmin,
    isEditor,
    isViewer,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
